const headers = {
  'Content-Type': 'application/json',
};

const timeout = 100000;

const parseBooleanEnv = (value: string | undefined): boolean | undefined => {
  if (!value) return undefined;

  const normalized = value.trim().toLowerCase();
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  return undefined;
};

export const resolveDisableRecaptcha = (
  isDev: boolean | undefined,
  disableRecaptcha: string | undefined,
): boolean | undefined => {
  // Only allow the recaptcha bypass during the local Vite dev server.
  if (!isDev) return undefined;

  return parseBooleanEnv(disableRecaptcha) ?? true;
};

// Environment-specific configuration
const envConfig = {
  prod: {
    baseURL: 'https://ria.api.wavvest.com/api/',
    featureFlagsBaseURL: 'https://flags.wavvest.com/api/',
    emailServiceBaseURL: 'https://email-service.wavvest.com/',
    riaAdminBaseURL: 'https://ria.api.wavvest.com/admin',
    weaveXBaseURL: 'https://weavex.wavvest.com/',
    weaveXWebSocketBaseURL: 'wss://weavex.wavvest.com/ws',
    plaidServiceBaseURL: 'https://plaidcash.wavvest.com/',
    stripePricingTableId: 'prctbl_1R9otDCtcN5a7emFwDxagf2K',
    stripePublishableKey:
      'pk_live_51R95qHCtcN5a7emF46Ub9BXFKn5waHdPugeUBvjL60H5maYI7sh1yHQU2BJVYok0wU5ic8dzvJT0p6ThmboWMB9Z00Sg4SHo6e',
    contactUsTopic: 'arn:aws:sns:us-west-2:867118425799:email_service_wavvest_prod',
    recaptchaSiteKey: '6Ld0UEwrAAAAAOuzaJCqelIoG33rMd6iUckOV2N_',
    recaptchaV3SiteKey: '6LdC6JQsAAAAADV0IOzoPtWzXnwJZNoV9OxttWjN',
  },
  dev: {
    baseURL: 'https://dev.ria.api.wavvestdev.com/api/',
    featureFlagsBaseURL: 'https://dev.flags.wavvestdev.com/api/',
    emailServiceBaseURL: 'https://dev.email-service.wavvestdev.com/',
    riaAdminBaseURL: 'https://dev.ria.api.wavvestdev.com/admin',
    weaveXBaseURL: 'https://dev.weavex.wavvestdev.com/',
    weaveXWebSocketBaseURL: 'wss://dev.weavex.wavvestdev.com/ws',
    plaidServiceBaseURL: 'https://dev.plaidcash.wavvestdev.com/',
    stripePricingTableId: 'prctbl_1SlvMgCwx59A3GzMAKpEC6So',
    stripePublishableKey:
      'pk_test_51Sj6SdCwx59A3GzMJ23RvCR1dmtolkgOb8aPMuYFcPKYYL5zgWG5wILqavS90X2B67UeQ0E0aydqNrfUlkr8IAjp00EU8gFQUo',
    contactUsTopic: 'arn:aws:sns:us-west-2:454138418563:email_service_wavvest_test',
    recaptchaSiteKey: '6LeqvAErAAAAAKPrwxyi0S5pz1E5KflBckczfJce',
    recaptchaV3SiteKey: '6LecZJQsAAAAAGb_LwJTIpHJiwL-MhSXCh38oi9j',
  },
  staging: {
    baseURL: 'https://staging.ria.api.wavveststg.com/api/',
    featureFlagsBaseURL: 'https://staging.flags.wavveststg.com/api/',
    emailServiceBaseURL: 'https://staging.email-service.wavveststg.com/',
    riaAdminBaseURL: 'https://staging.ria.api.wavveststg.com/admin',
    weaveXBaseURL: 'https://staging.weavex.wavveststg.com/',
    weaveXWebSocketBaseURL: 'wss://staging.weavex.wavveststg.com/ws',
    plaidServiceBaseURL: 'https://staging.plaidcash.wavveststg.com/',
    stripePricingTableId: 'prctbl_1R9782CdpP1fxh5Lf42UMxiN',
    stripePublishableKey:
      'pk_test_51R95qOCdpP1fxh5L5JoQOOFCt6MtqHq5GF72aD0Lj6HqfmhbE7bzyVBXSOhMX4nolX6AgV0RWbwyxN1Den58W5oK008ey3fhRC',
    contactUsTopic: 'arn:aws:sns:us-west-2:970547376400:email_service_wavvest_test',
    recaptchaSiteKey: '6LeqvAErAAAAAKPrwxyi0S5pz1E5KflBckczfJce',
    recaptchaV3SiteKey: '6LebZJQsAAAAAEzVv74TlpfCjB5RaVRwdALAR4In',
  },
};
type EnvironmentKey = keyof typeof envConfig;
type LocalDevProxyOverrides = Partial<
  Pick<(typeof envConfig)[EnvironmentKey], 'baseURL' | 'featureFlagsBaseURL' | 'weaveXBaseURL'>
>;

export const resolveLocalDevProxyOverrides = (
  isDevServer: boolean | undefined,
  environment: EnvironmentKey,
): LocalDevProxyOverrides => {
  if (!isDevServer || environment !== 'dev') return {};

  return {
    baseURL: '/api/',
    featureFlagsBaseURL: '/feature-flags-api/',
    weaveXBaseURL: '/weavex-api/',
  };
};

// Determine current environment
const getEnvironment = (): EnvironmentKey => {
  const mode = import.meta.env.MODE;

  // If mode is explicitly set, use it
  if (mode === 'dev') return 'dev';
  if (mode === 'staging') return 'staging';
  if (mode === 'production') return 'prod';

  // PROD is true for any build, false for dev server
  if (import.meta.env.PROD) return 'prod';

  // Default to dev for dev server or unknown modes
  return 'dev';
};

// Get configuration for current environment
const currentEnv = getEnvironment();
const localDevProxyOverrides = resolveLocalDevProxyOverrides(import.meta.env.DEV, currentEnv);
const envOverrides = {
  baseURL: import.meta.env.VITE_API_BASE_URL?.trim() || localDevProxyOverrides.baseURL,
  featureFlagsBaseURL:
    import.meta.env.VITE_FEATURE_FLAGS_BASE_URL?.trim() ||
    localDevProxyOverrides.featureFlagsBaseURL,
  riaAdminBaseURL: import.meta.env.VITE_RIA_ADMIN_BASE_URL?.trim(),
  weaveXBaseURL:
    import.meta.env.VITE_WEAVEX_BASE_URL?.trim() || localDevProxyOverrides.weaveXBaseURL,
  weaveXWebSocketBaseURL: import.meta.env.VITE_WEAVEX_WEBSOCKET_BASE_URL?.trim(),
  plaidServiceBaseURL: import.meta.env.VITE_PLAID_SERVICE_BASE_URL?.trim(),
  recaptchaSiteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY?.trim(),
  recaptchaV3SiteKey: import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY?.trim(),
  disableRecaptcha: resolveDisableRecaptcha(
    import.meta.env.DEV,
    import.meta.env.VITE_DISABLE_RECAPTCHA,
  ),
};
const config = {
  ...envConfig[currentEnv],
  ...Object.fromEntries(Object.entries(envOverrides).filter(([, value]) => Boolean(value))),
};

const idleTimeout = 30 * 60 * 1000;
const idleWarningTime = 2 * 60 * 1000;

export default {
  ...config,
  headers,
  timeout,
  idleTimeout,
  idleWarningTime,
};
